import React, {FunctionComponent, useEffect, useState} from "react";
import clsx from "clsx";
import Icon, {IconProps} from "../snippet/icon/Icon";
import FlexContent, {FlexContentDirection, FlexContentSpacing} from "../snippet/flex-content/FlexContent";

type TabHeaderItem = {
    label: string;
    icon?: IconProps | React.ReactElement;
}

type TabsProps = {
    headers: TabHeaderItem[];
    children: React.ReactNode;
    current?: number;
};

const Tabs: FunctionComponent<TabsProps> = ({headers, children, current = 0}) => {
    const [activeTab, setActiveTab] = useState(current);
    const panels = Array.isArray(children) ? children : [children];

    useEffect(() => {
        if (current !== undefined) {
            let newActiveTab = Math.max(0, current);
            newActiveTab = Math.min(panels.length - 1, newActiveTab);
            setActiveTab(newActiveTab);
        }
    }, [current]);

    const getIcon = (item: TabHeaderItem) => {
        if (!item.icon) {
            return null;
        }

        if (React.isValidElement(item.icon)) {
            return <span className="tabs__header__title__icon">{item.icon}</span>;
        } else {
            const icon = item.icon as IconProps;
            if (icon.name) {
                return <Icon {...icon}/>;
            }
        }
    };

    return (
        <FlexContent direction={FlexContentDirection.COLUMN} spacing={FlexContentSpacing.LARGE_PLUS} cssClass="tabs">
            <ul className="tabs__headers">
                {headers.map((it, index) =>
                    <li
                        key={`tab-${it.label}-${index}`}
                        className={clsx("tabs__header", {"tabs__header--active": activeTab === index})}
                        onClick={() => setActiveTab(index)}
                    >
                        <span className="tabs__header__title">
                            {getIcon(it)}
                            <span className="tabs__header__title__label">{it.label}</span>
                        </span>
                    </li>
                )}
            </ul>
            <div className="tabs__panels">
                {headers.map((it, index) =>
                    <div
                        key={`tab-${it.label}-${index}`}
                        className={clsx("tabs__panel", {"tabs__panel--active": activeTab === index})}
                    >
                        {panels[index]}
                    </div>
                )}
            </div>
        </FlexContent>
    );
};

export default Tabs;
